import React, { useState, useEffect, useRef } from 'react'
import { useNetworkStatus } from '../../utils/networkStatus'
import MessageModal from '../MessageModal/MessageModal'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'

export const ConnectionBanner = ({ route }) => {
    const { isOffline } = useNetworkStatus()
    const hasShownBannerRef = useRef(false)
    const [showOfflineBanner, setShowOfflineBanner] = useState(false)
    const [showRestrictedBanner, setShowRestrictedBanner] = useState(false)
    const [showCreateRequestBanner, setShowCreateRequestBanner] = useState(
        false,
    )
    const [offlineBannerShown, setOfflineBannerShown] = useState(false)

    const history = useHistory()

    const isLoginRoute = window.location.pathname.includes('/login')
    const isCreateRequestRoute = route.includes('/allRequests/create')
    const isRestrictedFeatureRoute = [
        '/equipment',
        '/requests/rental',
        '/users',
        '/closedPM',
        '/openPM',
        '/closedRequest',
        '/create/fleet/asset',
    ].some(r => route.startsWith(r))

    useEffect(() => {
        if (!isOffline) {
            // Connection restored - hide all banners and reset states
            hasShownBannerRef.current = false
            setShowOfflineBanner(false)
            setShowRestrictedBanner(false)
            setShowCreateRequestBanner(false)
            setOfflineBannerShown(false)
            return
        }

        if (isCreateRequestRoute && isOffline && !hasShownBannerRef.current) {
            setShowCreateRequestBanner(true)
            hasShownBannerRef.current = true
        }

        if (isRestrictedFeatureRoute && isOffline) {
            setShowRestrictedBanner(true)
        } else {
            setShowRestrictedBanner(false)
        }

        // Handle offline banner visibility
        const storedShowOfflineBanner = localStorage.getItem(
            'showOfflineBanner',
        )
        if (
            !offlineBannerShown &&
            storedShowOfflineBanner !== 'false' &&
            isOffline &&
            !isCreateRequestRoute &&
            !isRestrictedFeatureRoute &&
            !isLoginRoute
        ) {
            setShowOfflineBanner(true)
            setOfflineBannerShown(true)
        }
    }, [
        isOffline,
        isCreateRequestRoute,
        isRestrictedFeatureRoute,
        offlineBannerShown,
    ])

    const handleContinue = isRestricted => {
        if (isRestricted) {
            setShowRestrictedBanner(false)
            history.push('/dashboard')
            setOfflineBannerShown(true)
        } else if (showCreateRequestBanner) {
            setShowCreateRequestBanner(false)
        } else {
            setShowOfflineBanner(false)
        }
    }

    const handleShowAgain = () => {
        localStorage.setItem('showOfflineBanner', 'false')
        setShowOfflineBanner(false)
    }

    const title = showCreateRequestBanner
        ? 'It looks like you’re offline'
        : showRestrictedBanner
        ? 'No connection found'
        : 'No connection found'

    const message = showCreateRequestBanner ? (
        <>
            Don’t worry, we will save your request.
            <br />
            It will be processed once you’re back online.
        </>
    ) : showRestrictedBanner ? (
        "You need an internet connection to use this feature. Please try again when you're online."
    ) : (
        'You seem to be offline. You can continue browsing offline or try reconnecting to the internet.'
    )

    const textDone = showRestrictedBanner ? 'Go to Dashboard' : 'Continue'
    const showCancelButton = !showCreateRequestBanner && !showRestrictedBanner

    // Only show the relevant banner
    if (!showOfflineBanner && !showRestrictedBanner && !showCreateRequestBanner)
        return null

    return (
        <MessageModal
            isOpen={true}
            title={title}
            message={message}
            textDone={textDone}
            onDone={() => handleContinue(showRestrictedBanner)}
            textCancel={showCancelButton ? 'Don’t show again' : undefined}
            onCancel={showCancelButton ? handleShowAgain : undefined}
            maxWidth="xl"
        />
    )
}

ConnectionBanner.propTypes = {
    route: PropTypes.string.isRequired,
}
